"use client";

import { AnimatePresence, motion } from "motion/react";
import { Check, Facebook, Link2, Mail, Share2, Twitter } from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createLogger } from "@jesus-central-church/logger";

const logger = createLogger("share-dropdown");

interface ShareDropdownProps {
  title: string;
  text: string;
  url: string;
}

export default function ShareDropdown({
  title,
  text,
  url,
}: ShareDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const firstLinkRef = useRef<HTMLAnchorElement>(null);
  const dropdownId = useId();

  // Handle Escape key and focus management
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    // Focus first item when dropdown opens
    setTimeout(() => firstLinkRef.current?.focus(), 0);

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  // Handle click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !triggerRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logger.error("Failed to copy link", { error: err });
    }
  }, [url]);

  const handleNativeShare = useCallback(async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        setIsOpen(false);
      } catch (err) {
        logger.error("Native share failed", { error: err });
      }
    } else {
      setIsOpen(!isOpen);
    }
  }, [title, text, url, isOpen]);

  const shareLinks = [
    {
      name: "Facebook",
      icon: Facebook,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      color: "hover:bg-blue-500/20 hover:text-blue-400",
    },
    {
      name: "Twitter",
      icon: Twitter,
      url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
      color: "hover:bg-sky-500/20 hover:text-sky-400",
    },
    {
      name: "Email",
      icon: Mail,
      url: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(text + " " + url)}`,
      color: "hover:bg-red-500/20 hover:text-red-400",
    },
  ];

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        onClick={handleNativeShare}
        className="bg-strategy-gold text-freedom hover:bg-strategy-gold/90 flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-strategy-gold focus:ring-offset-2"
        aria-label="Delen"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-controls={isOpen ? dropdownId : undefined}
      >
        <Share2 className="h-4 w-4" />
      </button>

      <AnimatePresence>
        {isOpen && !navigator.share && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
              aria-hidden="true"
            />

            {/* Dropdown */}
            <motion.div
              ref={dropdownRef}
              id={dropdownId}
              role="menu"
              aria-label="Deel opties"
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              transition={{ duration: 0.15 }}
              className="bg-boldness/95 absolute right-0 bottom-full z-50 mb-2 w-48 overflow-hidden rounded-xl shadow-2xl backdrop-blur-xl"
            >
              <div className="p-2">
                {shareLinks.map((link, index) => (
                  <a
                    key={link.name}
                    ref={index === 0 ? firstLinkRef : undefined}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    role="menuitem"
                    className={`text-freedom flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-inset focus:ring-strategy-gold ${link.color}`}
                    onClick={() => setIsOpen(false)}
                  >
                    <link.icon className="h-4 w-4" aria-hidden="true" />
                    <span>{link.name}</span>
                  </a>
                ))}

                <button
                  onClick={handleCopyLink}
                  role="menuitem"
                  className="text-freedom hover:bg-strategy-gold/20 hover:text-strategy-gold flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-inset focus:ring-strategy-gold"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4" aria-hidden="true" />
                      <span aria-live="polite">Gekopieerd!</span>
                    </>
                  ) : (
                    <>
                      <Link2 className="h-4 w-4" aria-hidden="true" />
                      <span>Kopieer link</span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
